/**
 * Адаптер HUD боя к оболочке C2: `registerHud(Hud)` (пропс `snapshot`), игрок — из `appStore`, тексты — i18n
 * (`useT`, свои ключи подключены `addMessages`). Снимок + игрок → {@link toHudModel} → {@link HudView}.
 *
 * Колбэки: «открыть панель» — окна оболочки (серия → окно `streak`); команды боя (навык, «Автонавыки») идут в
 * источник боя, который подключает main/I1 через {@link connectHud} — у оболочки очереди команд нет.
 */
import type { PlayerCommand } from "@newfungame/contracts";
import { activeBalance } from "../../app/balance";
// не через "../../app": оболочка (App.tsx) сама импортирует этот файл как HUD по умолчанию — без цикла модулей
import { ftueStore, noteTimerSeen, vipTeaser } from "../../app/ftue";
import { heroIntroStore } from "../../app/hero-intro";
import { useStore, useT } from "../../app/hooks";
import type { HudViewProps as SlotProps } from "../../app/slots";
import {
  adminTools,
  appStore,
  currentWindow,
  cycleAdminSpeed,
  openWindow,
  retryLockOf,
  setAutoPref,
  setSpeedPref,
  speedOpen,
} from "../../app/store";
import { addMessages } from "../../i18n";
import { useEffect } from "preact/hooks";
import {
  completeHint,
  hintsStore,
  setSkillHintShown,
  skillHintTarget,
  SKILL_HINT_SNOOZE_MS,
  snoozeSkillHint,
} from "../hints";
import { HeroIntroPlates } from "./HeroIntro";
import { HudView } from "./HudView";
import { StoryPlate } from "./StoryPlate";
import { hudMessages, type HudT } from "./messages";
import { ParcelLayer } from "./parcel/ParcelLayer";
import { toHudModel, type HudActions, type HudPanel } from "./model";

addMessages(hudMessages);

/** Куда HUD шлёт команды боя: фейковый поток сейчас, очередь engine в I1. */
export type HudCommandSink = (cmd: PlayerCommand) => void;

let sink: HudCommandSink = () => {};
let addAdminCoins = (): void => {};

/** Подключить команды HUD к бою. Без подключения тапы ничего не делают. */
export function connectHud(send: HudCommandSink, coins: () => void = () => {}): void {
  addAdminCoins = coins;
  sink = send;
}

/** Окно оболочки для боковой иконки; `null` — в v1 окна нет (иконка и не показывается, см. `SIDE_UNLOCK`). */
function openPanel(panel: HudPanel): void {
  if (panel === "streak") openWindow({ kind: "streak" });
  if (panel === "tasks") openWindow({ kind: "tasks" });
  if (panel === "season") openWindow({ kind: "season" });
  if (panel === "rank") openWindow({ kind: "league" });
  if (panel === "friends") openWindow({ kind: "league", tab: "friends" });
}

export const hudActions: HudActions = {
  onCastSkill: (heroId) => {
    completeHint("skill");
    sink({ type: "castSkill", heroId });
  },
  onTapParcel: (parcelId) => {
    completeHint("parcel");
    sink({ type: "tapParcel", parcelId });
  },
  onToggleAuto: (enabled) => {
    setAutoPref(enabled);
    sink({ type: "setAutoSkills", enabled });
  },
  onToggleSpeed: (fast) => setSpeedPref(fast ? 2 : 1),
  onAdminSpeed: () => cycleAdminSpeed(),
  onAdminCoins: () => addAdminCoins(),
  onLocked: (place) => openWindow({ kind: "vip", place }),
  onOpen: openPanel,
};

export function Hud({ snapshot }: SlotProps) {
  const player = useStore(appStore, (s) => s.player);
  const fast = useStore(appStore, (s) => s.speedPref >= 2);
  const adminSpeed = useStore(appStore, (s) => (adminTools(s) ? s.speedPref : null));
  const t = useT() as HudT;
  const windowOpen = useStore(appStore, (s) => currentWindow(s) !== null);
  const skillDone = useStore(hintsStore, (s) => s.done.skill || s.skillSnoozed);
  const skillOff = skillDone || windowOpen;
  const speed = useStore(appStore, speedOpen);
  const lockNow = useStore(appStore, (s) => retryLockOf(s) !== null);
  const timerSeen = useStore(ftueStore, (f) => f.timerSeen);
  useEffect(() => {
    if (lockNow) noteTimerSeen();
  }, [lockNow]);
  // как у боковых иконок: дошёл до волны n — значит n − 1 пройдена
  const teaser = vipTeaser({
    timerSeen,
    lockNow,
    bestWave: Math.max(player?.bestWave ?? 0, snapshot.wave.number - 1),
  });
  const model = toHudModel(snapshot, player, fast, speed, activeBalance().heroes, adminSpeed, teaser);
  const hintSkill = skillHintTarget(model.skills, skillOff);
  const shown = hintSkill !== null;
  useEffect(() => {
    setSkillHintShown(shown);
    if (!shown) return;
    const timer = setTimeout(() => snoozeSkillHint(), SKILL_HINT_SNOOZE_MS);
    return () => clearTimeout(timer);
  }, [shown]);
  const introPulses = useStore(heroIntroStore, (i) => i?.pulses);
  return (
    <>
      <HudView model={model} actions={hudActions} t={t} hintSkill={hintSkill} introPulses={introPulses}>
        <ParcelLayer onTap={hudActions.onTapParcel} t={t} />
      </HudView>
      <HeroIntroPlates t={t} />
      <StoryPlate t={t} wave={model.wave.number} />
    </>
  );
}
